import { Component, OnInit, Inject, Output, EventEmitter, OnDestroy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from "@angular/material/dialog";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import {
  MudancaPlanoService,
  VerificarElegibilidadeResponse,
  MudarPlanoResponse,
  MudarPlanoRequest
} from "src/app/services/aluno/mudanca-plano.service";
import { AuthService } from "src/app/security/auth.service";
import { FormService } from "src/app/services/form.service";
import { AnimationsService } from "src/app/services/animations.service";
import { MatriculaAlunoService } from "src/app/services/aluno/matricula-aluno.service";
import { PlanoPagamentoService } from "src/app/services/gerenciador/plano-pagamento.service";
import { TefService } from "src/app/services/tef/tef.service";
import { Store, select } from "@ngrx/store";
import { FinanceiroStoreState, FinanceiroStoreSelectors } from "src/app/_store/financeiro-store";
import { Subscription } from "rxjs";
import {
  MppMultiplosPagamentosComponent,
  DadosMultiplosPagamentos
} from "../matricula-painel-pagamento/mpp-multiplos-pagamentos/mpp-multiplos-pagamentos.component";

@Component({
  selector: "app-matricula-mudanca-plano",
  templateUrl: "./matricula-mudanca-plano.component.html",
  styleUrls: ["./matricula-mudanca-plano.component.scss"]
})
export class MatriculaMudancaPlanoComponent implements OnInit, OnDestroy {
  @Output() onEfetuadoAcao: EventEmitter<any> = new EventEmitter();

  form: FormGroup;
  isLoading = false;
  sendingPlanos = false;
  sending = false;
  error = false;
  elegibilidade: VerificarElegibilidadeResponse = null;
  verificado = false;
  planosDisponiveis: any[] = [];
  matriculaId: number = null;
  unidadeLogadoId: number = null;
  cursoId: number = null;
  usuarioLogadoId: number = null;
  token: any = null;
  alunoId: number = null;
  financeiroCadastrado: any = null;
  financeiro$: Subscription = null;

  tiposPagamento = [
    { id: 1, label: "Cartão de Crédito" },
    { id: 2, label: "Cartão de Débito" },
    { id: 8, label: "PIX" }
  ];

  constructor(
    public dialogRef: MatDialogRef<MatriculaMudancaPlanoComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
    private fb: FormBuilder,
    private mudancaPlanoService: MudancaPlanoService,
    private authService: AuthService,
    private formService: FormService,
    private animationsService: AnimationsService,
    private matriculaAlunoService: MatriculaAlunoService,
    private planoPagamentoService: PlanoPagamentoService,
    private tefService: TefService,
    private storeFinanceiro: Store<FinanceiroStoreState.Financeiro>,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.matriculaId = this.data?.matriculaId;
    this.unidadeLogadoId = this.data?.unidadeLogadoId;
    this.token = JSON.parse(window.localStorage.getItem("accessToken"));
    this.usuarioLogadoId = this.token?.user?.id;
    this.buildForm();

    if (this.matriculaId) {
      this.carregarDadosMatricula();
    }
  }

  carregarDadosMatricula(): void {
    this.matriculaAlunoService.buscarPorId(this.matriculaId).subscribe(
      (matricula: any) => {
        this.cursoId = matricula?.cursoId;
        this.alunoId = matricula?.alunoId;
      },
      (erro) => {
        console.error("Erro ao carregar dados da matrícula", erro);
      }
    );

    // Buscar financeiro do store
    this.financeiro$ = this.storeFinanceiro
      .pipe(select(FinanceiroStoreSelectors.selectFinanceiroCadastrado))
      .subscribe((financeiro: any) => {
        this.financeiroCadastrado = financeiro;
      });
  }

  ngOnDestroy(): void {
    if (this.financeiro$) {
      this.financeiro$.unsubscribe();
    }
  }

  buildForm(): void {
    this.form = this.fb.group({
      tipoPagamentoNovo: ["", Validators.required],
      novoPlanoPagamentoId: ["", Validators.required],
      tefNsu: [""],
      tefAutorizacao: [""],
      tefValor: [""],
      tefParcelas: [""],
      observacao: [""]
    });
  }

  verificarElegibilidade(): void {
    if (!this.matriculaId) {
      this.error = true;
      return;
    }

    this.isLoading = true;
    this.mudancaPlanoService
      .verificarElegibilidade(this.matriculaId, this.unidadeLogadoId)
      .subscribe(
        (response: VerificarElegibilidadeResponse) => {
          this.elegibilidade = response;
          this.verificado = true;
          this.isLoading = false;

          if (!response.elegivel) {
            this.error = true;
          }
        },
        (erro) => {
          this.isLoading = false;
          this.error = true;
          console.error("Erro ao verificar elegibilidade", erro);
        }
      );
  }

  onTipoPagamentoChange(tipoPagamento: number): void {
    if (!tipoPagamento || !this.cursoId) {
      this.planosDisponiveis = [];
      this.form.get("novoPlanoPagamentoId").reset();
      return;
    }

    this.sendingPlanos = true;
    const filtros = {
      formaPagamento: tipoPagamento,
      cursoId: this.cursoId,
      unidadeId: this.unidadeLogadoId
    };

    this.planoPagamentoService
      .getPlanoPagamento(filtros)
      .subscribe(
        (planos: any[]) => {
          this.planosDisponiveis = planos || [];
          this.sendingPlanos = false;
          this.form.get("novoPlanoPagamentoId").reset();
        },
        (erro) => {
          this.sendingPlanos = false;
          console.error("Erro ao buscar planos", erro);
          this.animationsService.showErrorSnackBar(
            "Erro ao buscar planos disponíveis"
          );
        }
      );
  }

  executarMudancaPlano(): void {
    if (!this.form.valid || !this.elegibilidade?.elegivel) {
      return;
    }

    this.sending = true;
    const tipoPagamento = this.form.value.tipoPagamentoNovo;

    // Parcelas em aberto que serão canceladas quando o TEF confirmar
    const parcelasEmAberto = this.extrairParcelasEmAberto();

    if (parcelasEmAberto.length === 0) {
      this.sending = false;
      this.animationsService.showErrorSnackBar(
        "Nenhuma parcela em aberto para processar"
      );
      return;
    }

    // Valor do novo plano em centavos (não soma parcelas antigas)
    const valorTotal = Math.round(this.planoSelecionado.valorTotalPlano * 100);

    // Mapeamento de tipo de transação TEF (1=crédito, 2=pix, 0=débito)
    const tipoTransacao = tipoPagamento === 1 ? 1 : (tipoPagamento === 8 ? 2 : 0);

    // Payload para o tef-api — NovoPlanoPagamentoId sinaliza rota ExecutarMudancaPlano
    // O tef-api processa o terminal e chama api/MudancaPlano/executar-com-tef após aprovação
    const dadosPagamento = {
      valorTotal,
      tipoTransacao,
      novoPlanoPagamentoId: this.form.value.novoPlanoPagamentoId,
      parcelasParaCancelar: parcelasEmAberto.map(p => p.id),
      matriculaId: this.matriculaId,
      motivoMudancaPlano: this.form.value.observacao || null,
      usuarioLogadoId: this.usuarioLogadoId
    };

    this.tefService.transacaoTef(dadosPagamento).subscribe(
      (response) => {
        this.sending = false;

        if (response != null && response?.status !== 'error' && (response?.codigo === 0 || response?.codigo === undefined)) {
          // tef-api executou tudo atomicamente — fechar modal e atualizar painel
          this.animationsService.showSuccessSnackBar(
            "Mudança de plano realizada com sucesso!"
          );
          this.onEfetuadoAcao.emit({ tipo: "mudanca-plano" });
          this.dialogRef.close({ sucesso: true });
        } else {
          this.error = true;
          const msg = response?.status === 'error'
            ? 'Falha na comunicação com TEF. Nenhum pagamento foi registrado.'
            : 'Pagamento não aprovado. Mudança de plano cancelada.';
          this.animationsService.showErrorSnackBar(msg);
        }
      },
      (erro) => {
        this.sending = false;
        this.error = true;
        console.error("Erro ao processar pagamento", erro);
        this.animationsService.showErrorSnackBar(
          "Erro ao processar pagamento. Tente novamente."
        );
      }
    );
  }

  private extrairParcelasEmAberto(): any[] {
    if (!this.financeiroCadastrado?.pagamento) return [];

    // Filtrar apenas parcelas em aberto (tipoSituacao === 2 = À vencer)
    const parcelasEmAberto = this.financeiroCadastrado.pagamento.filter(
      (parcela: any) => parcela.tipoSituacao === 2 && parcela.valor !== 0
    );

    // Verificar se há alguma parcela paga (tipoSituacao === 1)
    const temParcelaPaga = this.financeiroCadastrado.pagamento.some(
      (parcela: any) => parcela.tipoSituacao === 1
    );

    if (temParcelaPaga) {
      this.animationsService.showErrorSnackBar(
        "Mudança de plano não permitida: existe parcela(s) paga(s)"
      );
      return [];
    }

    return parcelasEmAberto;
  }

  private calcularValorTotal(parcelas: any[]): number {
    let total = 0;
    parcelas.forEach((parcela: any) => {
      const valor = parcela.tipoSituacao === 2 ? parcela.valorVencimento : parcela.valor;
      if (valor && valor.toString().indexOf('.') > -1) {
        const casas = valor.toString().split('.');
        total += parseInt(`${casas[0]}${casas[1].slice(0, 2).padEnd(2, '0')}`);
      } else {
        total += parseInt(`${valor}00`);
      }
    });
    return total;
  }

  private executarMudancaPlanoAposPagamento(): void {
    const request: MudarPlanoRequest = {
      matriculaAlunoId: this.matriculaId,
      novoPlanoPagamentoId: this.form.value.novoPlanoPagamentoId,
      tipoPagamentoNovo: this.form.value.tipoPagamentoNovo,
      tefNsu: this.form.value.tefNsu,
      tefAutorizacao: this.form.value.tefAutorizacao,
      tefValor: this.form.value.tefValor,
      tefParcelas: this.form.value.tefParcelas,
      unidadeLogadoId: this.unidadeLogadoId,
      observacao: this.form.value.observacao
    };

    this.mudancaPlanoService
      .executarMudancaPlano(request)
      .subscribe(
        (response: MudarPlanoResponse) => {
          if (response.sucesso) {
            this.animationsService.showSuccessSnackBar(
              "Mudança de plano realizada com sucesso!"
            );
            this.onEfetuadoAcao.emit({ tipo: "mudanca-plano", response });
            this.dialogRef.close(response);
          } else {
            this.error = true;
            this.animationsService.showErrorSnackBar(response.mensagem);
          }
        },
        (erro) => {
          this.error = true;
          console.error("Erro ao executar mudança de plano", erro);
          this.animationsService.showErrorSnackBar(
            "Erro ao processar mudança de plano. Tente novamente."
          );
        }
      );
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  get planoSelecionado(): any {
    return this.planosDisponiveis.find(p => p.id === this.form.get('novoPlanoPagamentoId')?.value) ?? null;
  }

  get tipoPagamentoInicialParaMpp(): number {
    const tipo = this.form.get('tipoPagamentoNovo')?.value;
    return tipo === 8 ? 11 : (tipo ?? 1);
  }

  abrirMultiplosPagamentos(): void {
    const plano = this.planoSelecionado;
    if (!plano) return;

    const parcelasEmAberto = this.extrairParcelasEmAberto();

    const data: DadosMultiplosPagamentos = {
      valorTotal: plano.valorTotalPlano,
      matriculaId: this.matriculaId,
      cursoId: this.cursoId,
      planoPagamentoCursoId: plano.id,
      usuarioLogadoId: this.usuarioLogadoId,
      unidadeLogadoId: this.unidadeLogadoId,
      tipoPagamentoInicial: this.tipoPagamentoInicialParaMpp,
      maxParcelasCC: this.form.get('tipoPagamentoNovo')?.value === 1
        ? plano.quantidadeParcela
        : 1,
      isMudancaPlano: true,
      novoPlanoPagamentoId: plano.id,
      parcelasParaCancelar: parcelasEmAberto.map(p => p.id)
    };

    this.dialog.open(MppMultiplosPagamentosComponent, {
      width: '90vw',
      data,
      autoFocus: false
    }).afterClosed().subscribe(result => {
      if (!result?.finalizado) return;

      this.sending = true;
      this.mudancaPlanoService
        .registrarPosComposicao({
          matriculaAlunoId: this.matriculaId,
          novoPlanoPagamentoId: plano.id,
          composicaoId: result.composicaoId,
          parcelasParaCancelar: parcelasEmAberto.map(p => p.id),
          motivoMudancaPlano: this.form.value.observacao || null,
          usuarioLogadoId: this.usuarioLogadoId
        })
        .subscribe({
          next: () => {
            this.sending = false;
            this.animationsService.showSuccessSnackBar('Mudança de plano realizada com sucesso!');
            this.onEfetuadoAcao.emit({ tipo: 'mudanca-plano' });
            this.dialogRef.close({ sucesso: true });
          },
          error: (err) => {
            this.sending = false;
            this.animationsService.showErrorSnackBar(
              err?.error?.mensagem ?? 'Erro ao registrar mudança de plano'
            );
          }
        });
    });
  }
}
